import React, { useEffect, useState } from "react";

import { useFirebase } from "@meddbriefer/mdb-firebase"

// for development purposes, expose db to the dev console
// window.db = db

export const AuthContext = React.createContext({});


const PASSWORD_RESET_SUCCESS_MSG =
  "You should soon recieve an email with a password reset link it. " +
  "After reseting you password, you will be able to sign in."

// const EMAIL_VERIFY_MESSAGE =
//   "A message has been sent to your email address. It contains a link which will " +
//   "verify that you are the owner of that email address"

const ERROR_CODE_UNKNOWN_USER = "auth/user-not-found"
const ERROR_CODE_WRONG_PASSWD = "auth/wrong-password"
const ERROR_CODE_ACCT_ALREADY_EXISTS = "auth/email-already-in-use"
const ERROR_CODE_NETWORK_ERROR = "auth/network-request-failed"

const UNKNOWN_USER_ERROR_MSG = "Sorry, we have no record of a user with your email address"
const WRONG_PASSWD_ERROR_MSG = "Incorrect Password. Try again."
const ACCT_ALREADY_EXISTS_ERROR_MSG = "An account already exists with that email address"
const NETWORK_ERROR_MSG = "A Network Error has occurred. Try again later."


export const AUTH_FLOW_COMPONENTS = {
  LOGIN: "signin",
  REGISTER: "register",
  FORGOT_PASSWD: "forgot-passwd",
  EMAIL_VERIFY: "email-verify"
}


export function AuthProvider({children}) {
  const {auth, db} = useFirebase()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [userName, setUserName] = useState(null)
  const [authMsg, setAuthMsg] = useState("")
  const [currAuthComponent, setCurrAuthComponent] = useState(AUTH_FLOW_COMPONENTS.LOGIN)
  const [registerErrorMsg, setRegisterErrorMsg] = useState("")
  const [loginError, setLoginError] = useState("")
  const [passwdResetError, setPasswdResetError] = useState("")
  const [isStaff, setIsStaff] = useState(false)
  const [emailVerified, setEmailVerified] = useState(false)
  const [userPrefs, setUserPrefs] = useState({});


  const userIsStaff = async (user) => {
    // returns bool regarding whether user is authenticated and whether they have
    // "staff" privileges.
    // if a user has staff privs, a document will exist in the "staff" db collection
    // whose document id represents their uid
    if (!user) {
      return false;
    }
    const docRef = db.collection("staff").doc(user.uid);
    const doc = await docRef.get();
    return doc.exists;
  };

  const signOutUser = () => {
    return auth.signOut()
      .then(() => {
        setUnauthedState()
      })
      .catch((error) => console.log(error))
  }

  const setUnauthedState = () => {
    setIsAuthenticated(false)
    setIsStaff(false)
    setUserName(null)
    setEmailVerified(false)
  }

  const registerUserWithEmailAndPassword = (email, password, classCode) => {
    auth
      .createUserWithEmailAndPassword(email, password)
      .then((userCredential) => {
        // userCredential.user.sendEmailVerification();
        setRegisterErrorMsg("");
        return getUserRecord(userCredential.user.uid);
      })
      .catch((error) => {
        switch (error.code) {
          case ERROR_CODE_ACCT_ALREADY_EXISTS:
            setRegisterErrorMsg(ACCT_ALREADY_EXISTS_ERROR_MSG);
            break;
          case ERROR_CODE_NETWORK_ERROR:
            setRegisterErrorMsg(NETWORK_ERROR_MSG);
            break;
          default:
            // some error code I have yet to handle
            console.log(error.code);
            setRegisterErrorMsg(error.message);
            break;
        }
      })
      .then((userRec) => {
        updateUserRecord(userRec, "email", email);
        return userRec;
      })
      .then((userRec) => {
        updateUserRecord(userRec, "classCode", classCode);
        return userRec;
      })
      .then((userRec) =>
        console.log("users document created with email and classcode", userRec)
      );

  }

  const loginUserWithEmailAndPassword = (email, password) => {
    auth.signInWithEmailAndPassword(email, password)
      .then(() => {
        setLoginError("")
      })
      .catch((error) => {
        switch(error.code) {
          case ERROR_CODE_WRONG_PASSWD:
            setLoginError(WRONG_PASSWD_ERROR_MSG)
            break
          case ERROR_CODE_UNKNOWN_USER:
            setLoginError(UNKNOWN_USER_ERROR_MSG)
            break
          case ERROR_CODE_NETWORK_ERROR:
            setLoginError(NETWORK_ERROR_MSG)
            break
          default:
            // some error code I have yet to handle
            console.log(error.code)
            setLoginError(error.message)
            break
        }
      })
  }

  const sendOutPasswordReset = (email) => {
    auth.sendPasswordResetEmail(email)
      .then(() => {
        setAuthMsg(PASSWORD_RESET_SUCCESS_MSG)
        setCurrAuthComponent(AUTH_FLOW_COMPONENTS.LOGIN)
      })
      .catch((error) => {
        switch(error.code) {
          case ERROR_CODE_UNKNOWN_USER:
            setPasswdResetError(UNKNOWN_USER_ERROR_MSG)
            break
          case ERROR_CODE_NETWORK_ERROR:
            setPasswdResetError(NETWORK_ERROR_MSG)
            break
          default:
            // some error code I have yet to handle
            console.log(error.code)
            setPasswdResetError(error.message)
            break
        }
      }
    )
  }

  useEffect(() => {
    auth.onAuthStateChanged((user) => {
        // handle changes in auth state - does this include emailVerified changes???
        setCurrentUser(user)
        if (user) {
          setIsAuthenticated(true);
          // set userName to whatever is before the "@" in the user's email address
          setUserName(user.email.substr(0, user.email.indexOf("@")));
          // if (!user.emailVerified) {
          //   setAuthMsg(EMAIL_VERIFY_MESSAGE)
          //   setCurrAuthComponent(AUTH_FLOW_COMPONENTS.EMAIL_VERIFY)
          // } else {
          setEmailVerified(true);
          // }
          userIsStaff(user)
            .then((res) => {
              setIsStaff(res);
            })
            .catch((err) => console.error(err));
        } else {
          setUnauthedState()
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // only run on first render
  )

  const getUserRecord = (uid) => db.collection("users").doc(uid)

  const updateUserRecord = async (userDoc, field, value) => {
    await userDoc.set({
      [field]: value
    }, {merge: true})
  }
  // Return current users document
  const getUserDoc = () => db.collection("users").doc(currentUser.uid) || null;

  // Update or create user data in db
  const saveUserData = (field, data) => {
    return getUserDoc().set({
        [field]: data
    }, { merge: true })
  }

  const getUserData = async () => {
    let data = await getUserDoc().get();
    return data.data();
  }

  const getClassCodes = async () => {
    const ccSnapShot = await db.collection("class-codes").get()
    const classCodes = ccSnapShot.docs.map(doc => {
      const data = doc.data()
      return data.name
    })
    return classCodes
  }

  return (
    <AuthContext.Provider
      value={{
        auth,
        currentUser,
        authMsg,
        setAuthMsg,
        registerErrorMsg,
        setRegisterErrorMsg,
        registerUserWithEmailAndPassword,
        loginError,
        setLoginError,
        loginUserWithEmailAndPassword,
        passwdResetError,
        setPasswdResetError,
        sendOutPasswordReset,
        currAuthComponent,
        setCurrAuthComponent,
        signOutUser,
        userName,
        isAuthenticated,
        isStaff,
        emailVerified,
        setEmailVerified,
        userPrefs,
        setUserPrefs,
        saveUserData,
        getUserData,
        getClassCodes
      }}
    >
      {children}
    </AuthContext.Provider>
  )

}
